import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, ChevronDown } from "lucide-react";
import { clsx } from "clsx";
import { financialsApi } from "../../../lib/financialsApi";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { formatCurrency, formatDate } from "../../../utils/format";
import type { LedgerEvent } from "../../../lib/ledgerApi";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  category: string;
  label: string;
  from: string;
  to: string;
}

function EventRow({ event }: { event: LedgerEvent }) {
  const ts = event.eventDate ?? event.createdAt.slice(0, 10);
  return (
    <tr className="hover:bg-[#f8fafc]">
      <td className="px-4 py-3 text-xs text-[#64748b] whitespace-nowrap">{formatDate(ts)}</td>
      <td className="px-4 py-3 text-sm text-[#0f172a]">
        <p className="font-medium leading-tight">{event.description}</p>
        {event.employeeName && <p className="text-xs text-[#94a3b8]">{event.employeeName}</p>}
      </td>
      <td className="px-4 py-3 text-sm font-semibold text-[#0f172a] text-right whitespace-nowrap">
        {formatCurrency(event.amount)}
      </td>
    </tr>
  );
}

export function TransactionsDrawer({ isOpen, onClose, businessId, category, label, from, to }: Props) {
  const [offset, setOffset] = useState(0);
  const [allItems, setAllItems] = useState<LedgerEvent[]>([]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["financial-txns", businessId, category, from, to, offset],
    queryFn: async () => {
      const result = await financialsApi.getTransactions(businessId, from, to, category, offset);
      setAllItems((prev: LedgerEvent[]) => offset === 0 ? result.items : [...prev, ...result.items]);
      return result;
    },
    enabled: isOpen && !!businessId && !!category,
    staleTime: 60_000,
  });

  const handleClose = () => {
    setOffset(0);
    setAllItems([]);
    onClose();
  };

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 z-40 bg-black/30 transition-opacity duration-300",
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className={clsx(
          "fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-xl flex flex-col",
          "transform transition-transform duration-300",
          isOpen ? "translate-x-0" : "translate-x-full",
        )}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2e8f0] shrink-0">
          <div>
            <h2 className="text-base font-semibold text-[#0f172a]">{label}</h2>
            <p className="text-xs text-[#94a3b8]">{from} — {to} · {data?.total ?? 0} transaction{(data?.total ?? 0) !== 1 ? "s" : ""}</p>
          </div>
          <button onClick={handleClose} className="flex h-8 w-8 items-center justify-center rounded-lg text-[#64748b] hover:bg-[#f1f5f9] transition-colors">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && allItems.length === 0 ? (
            <div className="flex justify-center py-12"><LoadingSpinner /></div>
          ) : allItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <p className="text-sm font-medium text-[#64748b]">No transactions found</p>
              <p className="text-xs text-[#94a3b8] mt-0.5">for this category in the selected period.</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-[#f8fafc] border-b border-[#e2e8f0]">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">Date</th>
                    <th className="px-4 py-2.5 text-left text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">Description</th>
                    <th className="px-4 py-2.5 text-right text-[10px] font-semibold text-[#94a3b8] uppercase tracking-wide">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f1f5f9]">
                  {allItems.map((ev) => <EventRow key={ev.eventId} event={ev} />)}
                </tbody>
              </table>

              {data?.nextOffset !== undefined && (
                <div className="flex justify-center p-4">
                  <button
                    onClick={() => setOffset(data.nextOffset!)}
                    disabled={isFetching}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-4 py-2 text-xs font-medium text-[#475569] hover:bg-[#f1f5f9] transition-colors disabled:opacity-60"
                  >
                    {isFetching ? <LoadingSpinner size="sm" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
